import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Drawer,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DocumentItem from "../../../Components/Masters/DocumentItem";
import { formatDateToDDMMYYYY } from "../../../utils/formatDate";
import { getMonthName } from "../../../utils/getMonthName";

const isMissingConsumptionValue = (val) => {
  if (val === undefined || val === null || val === "" || val === "-") return true;
  if (val === 0 || val === "0") return true;
  const num = Number(val);
  return !Number.isNaN(num) && num === 0;
};

const formatWithUnit = (val, unit) => {
  if (val === undefined || val === null || val === "" || val === "-") return "-";
  const strVal = String(val);
  const unitStr = unit && unit !== "-" ? String(unit).trim() : "";
  return unitStr ? `${strVal} ${unitStr}` : strVal;
};

const getAssetSizeParts = (data) => {
  const raw = data?.assetSize ?? data?.assetId?.assetSize;
  let sizeVal = "";
  let sizeUnit =
    data?.assetSizeUnit ||
    data?.assetId?.assetSizeUnit ||
    "";

  if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) {
    sizeVal =
      raw.value !== undefined && raw.value !== null && raw.value !== ""
        ? raw.value
        : "";
    sizeUnit = sizeUnit || raw.unit || "";
  } else if (raw !== undefined && raw !== null && raw !== "" && raw !== "-") {
    sizeVal = raw;
  }

  return { sizeVal, sizeUnit };
};

function ViewData({
  title,
  inputData,
  element,
  viewData,
  setViewData,
  handleEdit,
  companyAdmin,
  setData,
}) {
  const [filteredData, setFilteredData] = useState({});

  useEffect(() => {
    if (element) {
      setFilteredData(element);
    }
  }, [element]);

  const list = () => (
    <Box
      display="flex"
      gap={3}
      sx={{
        width: "26.5rem",
        maxWidth: "100%",
        height: "100vh",
        justifyContent: "space-between",
        backgroundColor: "transparent",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#fff",
          height: "3%",
          padding: "13px",
          margin: "30px 0 0 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "80%",
          width: "5%",
        }}
      >
        <IconButton
          onClick={() => {
            setViewData(false);
            setData({});
          }}
          sx={{ color: "black" }}
        >
          <CloseIcon sx={{ fontSize: "1.2rem" }} />
        </IconButton>
      </Box>
      <Box
        sx={{ p: 3, width: "100%", backgroundColor: "#fff", overflow: "auto" }}
      >
        <Typography
          variant="h6"
          sx={{
            textDecoration: "underline",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          {title}
        </Typography>
        <Grid
          spacing={2}
          sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}
        >
          {inputData?.map((input) => {
            if (input.fieldName === "fuelConsumptionUnit") {
              return null;
            }

            let value = filteredData?.[input.fieldName];

            if (input.fieldName === "assetSize") {
              const { sizeVal, sizeUnit } = getAssetSizeParts(filteredData);
              value = formatWithUnit(sizeVal, sizeUnit);
            } else if (input.fieldName === "electricityConsumption") {
              const raw = filteredData?.electricityConsumption;
              const elecVal =
                raw === undefined || raw === null || raw === ""
                  ? null
                  : typeof raw === "object" && raw !== null
                    ? raw.value ?? raw
                    : raw;
              if (isMissingConsumptionValue(elecVal)) {
                value = "-";
              } else {
                const elecUnit =
                  filteredData?.electricityConsumptionUnit ||
                  (typeof raw === "object" && raw !== null ? raw.unit : "") ||
                  "";
                value = formatWithUnit(elecVal, elecUnit);
              }
            } else if (input.fieldName === "fuelConsumption") {
              const raw = filteredData?.fuelConsumption;
              const fuelVal =
                raw === undefined || raw === null || raw === ""
                  ? null
                  : typeof raw === "object" && raw !== null
                    ? raw.value ?? raw
                    : raw;
              if (isMissingConsumptionValue(fuelVal)) {
                value = "-";
              } else {
                const fuelUnit =
                  filteredData?.fuelConsumptionUnit ||
                  (typeof raw === "object" && raw !== null ? raw.unit : "");
                value = formatWithUnit(fuelVal, fuelUnit);
              }
            } else if (input.fieldName === "consumptionPeriod") {
              const month = value?.month;
              const year = value?.year;
              if (month && year) {
                value = `${getMonthName(month)} ${year}`;
              } else {
                value = "-";
              }
            } else if (input.fieldName === "locationCountry") {
              const raw =
                filteredData?.locationCountry ??
                filteredData?.assetId?.locationCountry ??
                "";
              value =
                raw === undefined || raw === null || String(raw).trim() === ""
                  ? "-"
                  : String(raw);
            } else if (value === undefined || value === null || value === "") {
              value = "-";
            } else if (typeof value === "object" && value !== null) {
              // Handle any other object types - convert to string representation
              value = JSON.stringify(value);
            } else {
              // Ensure all values are strings
              value = String(value);
            }

            if (["leaseStartDate", "leaseEndDate"].includes(input.fieldName)) {
              value = formatDateToDDMMYYYY(value);
            }

            return (
              <Grid item xs={12} sm={12} key={input.fieldName} mb={1}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: "bold",
                    wordBreak: "break-word",
                  }}
                >
                  {input.title}
                </Typography>
                <div style={{ wordBreak: "break-word" }}>
                  {typeof value === "string" || typeof value === "number" 
                    ? value 
                    : String(value || "-")}
                </div>
              </Grid>
            );
          })}
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Documents (optional)
          </Typography>
          {/* Render Images First */}
          {element && element?.documents?.length > 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              sx={{ width: "100%" }}
            >
              <Box display="flex" flexWrap="wrap" gap={1}>
                {element?.documents
                  .filter((url) =>
                    ["png", "jpg", "jpeg"].includes(
                      url.split(".").pop().toLowerCase()
                    )
                  )
                  ?.map((url, index) => (
                    <DocumentItem key={`image-${index}`} url={url} />
                  ))}
              </Box>

              {/* Render Other Documents */}
              <Box display="flex" flexWrap="wrap" gap={1}>
                {element?.documents
                  .filter(
                    (url) =>
                      !["png", "jpg", "jpeg"].includes(
                        url.split(".").pop().toLowerCase()
                      )
                  )
                  ?.map((url, index) => (
                    <DocumentItem key={`file-${index}`} url={url} />
                  ))}
              </Box>
            </Box>
          ) : (
            <Typography>-</Typography>
          )}
        </Box>

        {!companyAdmin && (
          <Button
            variant="contained"
            sx={{ mt: 2, backgroundColor: "#0a3f73", textTransform: "none" }}
            onClick={() => handleEdit(element)}
          >
            Edit
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      open={viewData}
      onClose={() => {
        setViewData(false);
        setData({});
      }}
      sx={{
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
        },
      }}
    >
      {list()}
    </Drawer>
  );
}

export default ViewData;
